import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useWeeklyMenu } from '@/hooks/useWeeklyMenu';
import { DayOfWeek, MealType } from '@/types';

interface AddToWeeklyMenuDialogProps {
  isOpen: boolean;
  onClose: () => void;
  recipeTitle: string;
  recipeId: number;
}

const AddToWeeklyMenuDialog = ({
  isOpen,
  onClose,
  recipeTitle,
  recipeId,
}: AddToWeeklyMenuDialogProps) => {
  const { toast } = useToast();
  const { addRecipeToMenu, loading, currentMenu } = useWeeklyMenu();
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(DayOfWeek.LUNES);
  const [selectedMeal, setSelectedMeal] = useState<MealType>(MealType.COMIDA);

  // Resetear selecciones cuando se abre el diálogo
  useEffect(() => {
    if (isOpen) {
      setSelectedDay(DayOfWeek.LUNES);
      setSelectedMeal(MealType.COMIDA);
    }
  }, [isOpen]);

  const handleAddToMenu = async () => {
    if (!currentMenu) {
      toast({
        title: "Error",
        description: "No se pudo cargar el menú semanal",
        variant: "destructive",
      });
      return;
    }

    const success = await addRecipeToMenu(selectedDay, selectedMeal, recipeId);

    if (success) {
      toast({
        title: "¡Éxito!",
        description: `${recipeTitle} se ha agregado al menú del ${getDaySpanish(selectedDay)} para ${getMealSpanish(selectedMeal)}`,
      });
      onClose();
    } else {
      toast({
        title: "Error",
        description: "No se pudo agregar la receta al menú",
        variant: "destructive",
      });
    }
  };

  // Mapeo de días a español
  const getDaySpanish = (day: DayOfWeek): string => {
    const daysMap = {
      [DayOfWeek.LUNES]: 'Lunes',
      [DayOfWeek.MARTES]: 'Martes',
      [DayOfWeek.MIERCOLES]: 'Miércoles',
      [DayOfWeek.JUEVES]: 'Jueves',
      [DayOfWeek.VIERNES]: 'Viernes',
      [DayOfWeek.SABADO]: 'Sábado',
      [DayOfWeek.DOMINGO]: 'Domingo',
    };
    return daysMap[day];
  };

  // Mapeo de tipos de comida a español
  const getMealSpanish = (meal: MealType): string => {
    const mealsMap = {
      [MealType.DESAYUNO]: 'Desayuno',
      [MealType.COMIDA]: 'Comida',
      [MealType.CENA]: 'Cena',
      [MealType.SNACK]: 'Snack',
    };
    return mealsMap[meal];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar al Menú Semanal</DialogTitle>
          <DialogDescription>
            Selecciona el día y tipo de comida para agregar "{recipeTitle}" a tu menú semanal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="day">Día de la semana</Label>
            <Select value={selectedDay} onValueChange={(value: DayOfWeek) => setSelectedDay(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un día" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DayOfWeek.LUNES}>Lunes</SelectItem>
                <SelectItem value={DayOfWeek.MARTES}>Martes</SelectItem>
                <SelectItem value={DayOfWeek.MIERCOLES}>Miércoles</SelectItem>
                <SelectItem value={DayOfWeek.JUEVES}>Jueves</SelectItem>
                <SelectItem value={DayOfWeek.VIERNES}>Viernes</SelectItem>
                <SelectItem value={DayOfWeek.SABADO}>Sábado</SelectItem>
                <SelectItem value={DayOfWeek.DOMINGO}>Domingo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="meal">Tipo de comida</Label>
            <Select value={selectedMeal} onValueChange={(value: MealType) => setSelectedMeal(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona tipo de comida" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MealType.DESAYUNO}>Desayuno</SelectItem>
                <SelectItem value={MealType.COMIDA}>Comida</SelectItem>
                <SelectItem value={MealType.CENA}>Cena</SelectItem>
                <SelectItem value={MealType.SNACK}>Snack</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleAddToMenu} disabled={loading}>
              {loading ? "Agregando..." : "Agregar al Menú"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToWeeklyMenuDialog;